import type { BreadcrumbProps } from '@blueprintjs/core'
import { computed, makeAutoObservable } from 'mobx'
import { observer } from 'mobx-react-lite'
import React, { Fragment, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { ActionBar } from '../components/ActionBar'
import { AdTypeBadge } from '../components/badge/AdTypeBadge'
import { StateBadge } from '../components/badge/StateBadge'
import { Actions } from '../components/details/UpdateActions'
import { KeywordsTable } from '../components/keywords/KeywordsTable'
import { NegativeKeywordsTable } from '../components/keywords/NegativeKeywordsTable'
import { Layout } from '../components/Layout'
import { ProductAdsTable } from '../components/product_ads/ProductAdsTable'
import { SearchTermsTable2 } from '../components/SearchTermsTable2'
import type { ExpandedPerformanceMetrics } from '../components/table2/ad_grid_columns'
import { TableStore } from '../components/table2/table_store'
import { TargetACOS } from '../components/TargetACOS'
import { NegativeTargetsTable } from '../components/targets/NegativeTargetsTable'
import { TargetsTable } from '../components/targets/TargetsTable'
import { TabbedCards } from '../components/ui/TabbedCards'
import { useSavedFiltersStore } from '../hooks/useSavedFiltersStore'
import { useLatestRequestEffect } from '../hooks/useLatestRequestEffect'
import { useUser } from '../hooks/useUser'
import { useTableHooksFromStore } from '../packages/blueprint-components/src/useTableHooks'
import { userStore } from '../stores/user_store'
import type { AdGroup } from '../types/ad_group'
import type { Keyword } from '../types/keyword'
import type { ProductAdPerformance } from '../types/product_ad_extended'
import type { TargetPerformance } from '../types/target'
import { axiosPatch } from '../util/api'
import { adManagerPerformanceGet } from '../util/adManagerPerformanceApi'
import { cents } from '../util/formatters'
import { OverviewCharts } from './dashboard-overview-charts'

/** Search term with nested performances from v5 API */
interface SearchTermItem {
    search_term: string
    type_of: string
    performances: Array<{
        id?: number | string
        ad_group_id: number
        ad_group_name: string
        campaign_id: number
        campaign_name: string
        type_of: string
    }>
}

interface SubStores {
    productAds: TableStore<ProductAdPerformance & ExpandedPerformanceMetrics>
    searchTerms: TableStore<SearchTermItem>
    keywords: TableStore<Keyword & ExpandedPerformanceMetrics>
    targets: TableStore<TargetPerformance & ExpandedPerformanceMetrics>
    negativeKeywords: TableStore<Models.NegativeKeywordExtended>
    negativeTargets: TableStore<Models.NegativeTargetExtended>
}

class DetailStore {
    activeTab: string = ''
    subStores: SubStores

    constructor(subStores: SubStores) {
        this.subStores = subStores
        makeAutoObservable(this)
    }

    setActiveTab(tab: string) {
        this.activeTab = tab
    }

    get dates(): { from: Date; to: Date } {
        switch (this.activeTab) {
            case 'ads':
                return {
                    from: this.subStores.productAds.params.from,
                    to: this.subStores.productAds.params.to,
                }
            case 'search_terms':
                return {
                    from: this.subStores.searchTerms.params.from,
                    to: this.subStores.searchTerms.params.to,
                }
            case 'keywords':
                return {
                    from: this.subStores.keywords.params.from,
                    to: this.subStores.keywords.params.to,
                }
            case 'targets':
                return {
                    from: this.subStores.targets.params.from,
                    to: this.subStores.targets.params.to,
                }
            case 'negative_keywords':
                return {
                    from: this.subStores.negativeKeywords.params.from,
                    to: this.subStores.negativeKeywords.params.to,
                }
            case 'negative_targets':
                return {
                    from: this.subStores.negativeTargets.params.from,
                    to: this.subStores.negativeTargets.params.to,
                }
            default:
                return {
                    // 30 days ago
                    from: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
                    to: new Date(),
                }
        }
    }

    @computed({ equals: (a: Date, b: Date) => a.getTime() / 86400 === b.getTime() / 86400 })
    get from() {
        return this.dates.from
    }

    @computed({ equals: (a: Date, b: Date) => a.getTime() / 86400 === b.getTime() / 86400 })
    get to() {
        return this.dates.to
    }
}

/**
 * Ad Group Detail Page (v5 - Core/PostgreSQL)
 *
 * Uses v5 API endpoints backed by Core/PostgreSQL for ad group data.
 * This is the only ad group detail implementation.
 */
export const AdGroupDetailV5 = observer(() => {
    const { ad_group_id } = useParams()
    const [adGroup, setAdGroup] = useState<AdGroup | null>(null)
    const { profile, searchParams } = useUser()
    const savedFiltersStore = useSavedFiltersStore()

    const {
        productAdsTableStore,
        searchTermsTableStore,
        keywordsTableStore,
        targetsTableStore,
        negativeKeywordsTableStore,
        negativeTargetsTableStore,
        detailStore,
    } = useMemo(() => {
        const productAds = new TableStore<ProductAdPerformance & ExpandedPerformanceMetrics>(
            userStore,
            `product_ads_for_ad_group_v5_${ad_group_id}`,
        )
        const searchTerms = new TableStore<SearchTermItem>(
            userStore,
            `search_terms_for_ad_group_v5_${ad_group_id}`,
        )
        const keywords = new TableStore<Keyword & ExpandedPerformanceMetrics>(
            userStore,
            `keywords_for_ad_group_v5_${ad_group_id}`,
        )
        const targets = new TableStore<TargetPerformance & ExpandedPerformanceMetrics>(
            userStore,
            `targets_for_ad_group_v5_${ad_group_id}`,
        )
        const negativeKeywords = new TableStore<Models.NegativeKeywordExtended>(
            userStore,
            `negative_keywords_for_ad_group_v5_${ad_group_id}`,
        )
        const negativeTargets = new TableStore<Models.NegativeTargetExtended>(
            userStore,
            `negative_targets_for_ad_group_v5_${ad_group_id}`,
        )

        return {
            productAdsTableStore: productAds,
            searchTermsTableStore: searchTerms,
            keywordsTableStore: keywords,
            targetsTableStore: targets,
            negativeKeywordsTableStore: negativeKeywords,
            negativeTargetsTableStore: negativeTargets,
            detailStore: new DetailStore({
                productAds,
                searchTerms,
                keywords,
                targets,
                negativeKeywords,
                negativeTargets,
            }),
        }
    }, [ad_group_id])

    /**
     * Creates a loader function for v5 API endpoints that filters by ad_group_id
     */
    const createLoader = useMemo(
        () => (endpoint: string) => (params: Record<string, unknown>) => {
            if (!profile) {
                return Promise.resolve()
            }
            return adManagerPerformanceGet(
                endpoint,
                {
                    ...params,
                    filter: {
                        ...(params.filter as Record<string, unknown>),
                        ad_group_id,
                    },
                },
                true,
            )
        },
        [profile, ad_group_id, searchParams],
    )

    const reloadMetadata = useLatestRequestEffect(
        () =>
            profile
                ? adManagerPerformanceGet(`api/v5/ad-groups/${ad_group_id}`, {}, true).then((res) => res.data)
                : undefined,
        (data) => {
            if (data) {
                setAdGroup(data)
            }
        },
        [profile, ad_group_id],
        undefined,
        () => setAdGroup(null),
    )

    // Updates still go through Laravel API
    const update = (payload: Record<string, unknown>) =>
        axiosPatch(`ad-groups/${ad_group_id}`, payload)

    // Standard loaders using the helper
    const loadProductAds = createLoader('api/v5/product-ads')
    const loadKeywords = createLoader('api/v5/keywords')
    const loadTargets = createLoader('api/v5/targets')
    const loadNegativeKeywords = createLoader('api/v5/negative-keywords')
    const loadNegativeTargets = createLoader('api/v5/negative-targets')

    // Search terms needs custom post-processing to flatten the nested structure
    const loadSearchTerms = (params: Record<string, unknown>) => {
        if (!profile) {
            return Promise.resolve()
        }

        return adManagerPerformanceGet(
            'api/v5/search-terms',
            {
                ...params,
                filter: {
                    ...(params.filter as Record<string, unknown>),
                    ad_group_id,
                },
            },
            true,
        )
            .then((res) => {
                // Flatten performance models into a single array
                const output: Record<string, unknown>[] = []

                res.data.data.forEach((searchTerm: SearchTermItem) => {
                    output.push({
                        ...searchTerm,
                        type_of: 'search_term',
                        original_type: searchTerm.type_of,
                    })

                    searchTerm?.performances?.forEach((item) => {
                        const itemWithKey = item as Record<string, unknown>
                        itemWithKey.select_key = `${item.type_of}_${item.id}`
                        itemWithKey.type_of = 'ad_group'
                        output.push(itemWithKey)
                    })
                })
                res.data.data = output

                return res
            })
            .catch((err: Error) => {
                console.error(err)
            })
    }

    const productAdTableHooks = useTableHooksFromStore(
        loadProductAds,
        {},
        false,
        productAdsTableStore,
    )
    const searchTermTableHooks = useTableHooksFromStore(
        loadSearchTerms,
        {},
        false,
        searchTermsTableStore,
    )
    const keywordTableHooks = useTableHooksFromStore(
        loadKeywords,
        {},
        false,
        keywordsTableStore,
    )
    const targetsTableHooks = useTableHooksFromStore(
        loadTargets,
        {},
        false,
        targetsTableStore,
    )
    const negativeKeywordsTableHooks = useTableHooksFromStore(
        loadNegativeKeywords,
        {},
        false,
        negativeKeywordsTableStore,
    )
    const negativeTargetsTableHooks = useTableHooksFromStore(
        loadNegativeTargets,
        {},
        false,
        negativeTargetsTableStore,
    )

    const actions = (
        <Fragment>
            <ActionBar className="flex items-center justify-between pl-2 pr-4">
                <div className="flex items-center">
                    {adGroup && (
                        <Fragment>
                            <AdTypeBadge {...adGroup} className="ml-2 mr-2" />
                            <StateBadge {...adGroup} className="mr-2" />
                            <div className="mr-2">Default Bid {cents(adGroup.default_bid)}</div>

                            <div className="flex justify-center mr-2">
                                <span className="mr-1">Target ACoS</span>
                                <TargetACOS
                                    target="ad_group"
                                    product={adGroup}
                                    mutateItems={(cb) => setAdGroup((prev) => cb([prev])[0])}
                                />
                            </div>
                        </Fragment>
                    )}
                </div>
                <Actions
                    name="Ad Group"
                    data={adGroup}
                    update={update}
                    reloadParent={reloadMetadata}
                />
            </ActionBar>
        </Fragment>
    )

    const breadcrumbs: BreadcrumbProps[] = [
        { text: 'Manage' },
        {
            text: `Campaign: ${adGroup?.campaign_name}`,
            href: `/campaigns/${adGroup?.campaign_id}`,
        },
        { text: 'Ad Group:' },
    ]

    return (
        <Layout breadcrumbs={breadcrumbs} primary={adGroup?.name} actions={actions}>
            <OverviewCharts
                params={{
                    type: 'ad_groups',
                    filter: { ad_group_id },
                    from: detailStore.from,
                    to: detailStore.to,
                    previous: true,
                }}
            />

            <TabbedCards
                tabs={[
                    'Ads',
                    'Search Terms',
                    'Keywords',
                    'Targets',
                    'Negative Keywords',
                    'Negative Targets',
                ]}
                onChange={(tab) => detailStore.setActiveTab(tab)}
            >
                <ProductAdsTable
                    nested={true}
                    store={productAdsTableStore}
                    savedFiltersStore={savedFiltersStore}
                    tableHooks={productAdTableHooks}
                    showCharts={false}
                />
                <SearchTermsTable2
                    nested={true}
                    tableHooks={searchTermTableHooks}
                    store={searchTermsTableStore}
                    savedFiltersStore={savedFiltersStore}
                />
                <KeywordsTable
                    nested={true}
                    store={keywordsTableStore}
                    savedFiltersStore={savedFiltersStore}
                    tableHooks={keywordTableHooks}
                    showCharts={false}
                />
                <TargetsTable
                    nested={true}
                    store={targetsTableStore}
                    savedFiltersStore={savedFiltersStore}
                    tableHooks={targetsTableHooks}
                    showCharts={false}
                />
                <NegativeKeywordsTable
                    nested={true}
                    store={negativeKeywordsTableStore}
                    savedFiltersStore={savedFiltersStore}
                    tableHooks={negativeKeywordsTableHooks}
                />
                <NegativeTargetsTable
                    nested={true}
                    store={negativeTargetsTableStore}
                    savedFiltersStore={savedFiltersStore}
                    tableHooks={negativeTargetsTableHooks}
                />
            </TabbedCards>
            <div className="mb-4" />
        </Layout>
    )
})
